import type { ResponseOf, RouteName } from "@checkone/contract/client";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { call, type ApiError, type CallOptions } from "./client";

type QueryExtra<N extends RouteName> = Omit<UseQueryOptions<ResponseOf<N>, ApiError>, "queryKey" | "queryFn">;

/** Query keyed by route name and options, so invalidating a route name refreshes every variant. */
export function useApi<N extends RouteName>(name: N, opts?: CallOptions<N>, extra?: QueryExtra<N>) {
  return useQuery<ResponseOf<N>, ApiError>({
    queryKey: [name, opts ?? {}],
    queryFn: ({ signal }) => call(name, { ...(opts ?? {}), signal } as CallOptions<N>),
    ...extra,
  });
}

/** Mutation that invalidates the listed route names on success. */
export function useApiMutation<N extends RouteName>(name: N, invalidate: readonly RouteName[] = []) {
  const qc = useQueryClient();
  return useMutation<ResponseOf<N>, ApiError, CallOptions<N>>({
    mutationFn: (opts) => call(name, opts),
    // Not awaited: per-call callbacks (toasts, closing dialogs) must run before the refetch can
    // unmount the component that issued the mutation.
    onSuccess: () => {
      void Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: [k] })));
    },
  });
}

export function useMe() {
  return useApi("auth.me", undefined, { retry: false, staleTime: 60_000 });
}
